import React, { useState, useRef, useEffect } from 'react';
import { messagingService } from '../../../services/messagingService';
import { 
  Send, 
  Paperclip, 
  Smile, 
  Phone, 
  MoreVertical,
  User,
  Users,
  Clock,
  Check,
  CheckCheck,
  AlertCircle
} from 'lucide-react';

const MessagePanel = ({ 
  conversation, 
  messages, 
  onSendMessage, 
  loading, 
  currentUser, 
  userProfile 
}) => {
  const [messageText, setMessageText] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [showPhoneInput, setShowPhoneInput] = useState(false);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const messageSubscriptionRef = useRef(null);

  // Auto scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Subscribe to real-time message updates for this conversation
  useEffect(() => {
    if (conversation?.id) {
      messageSubscriptionRef.current = messagingService?.subscribeToMessages(
        conversation?.id,
        (payload) => {
          // Handle real-time message updates
          if (payload?.eventType === 'INSERT') {
            // New message received - will be handled by parent component
          }
        }
      );
    }

    return () => {
      if (messageSubscriptionRef?.current) {
        messageSubscriptionRef?.current?.unsubscribe();
      }
    };
  }, [conversation?.id]);

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async (e) => {
    e?.preventDefault();
    if (!messageText?.trim() || sending) return;

    setSending(true);
    try {
      const smsPhoneNumber = showPhoneInput && phoneNumber ? phoneNumber : null;
      await onSendMessage(messageText?.trim(), smsPhoneNumber);
      setMessageText('');
      setPhoneNumber('');
      setShowPhoneInput(false);
    } catch (err) {
      console.error('Error sending message:', err);
    } finally {
      setSending(false);
    }
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp)?.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getMessageStatusIcon = (status) => {
    switch (status) {
      case 'sending':
        return <Clock className="w-3 h-3 text-gray-400" />;
      case 'sent':
        return <Check className="w-3 h-3 text-gray-500" />;
      case 'delivered':
        return <CheckCheck className="w-3 h-3 text-blue-500" />;
      case 'failed':
        return <AlertCircle className="w-3 h-3 text-red-500" />;
      default:
        return null;
    }
  };

  const getConversationTitle = () => {
    if (conversation?.title) return conversation?.title;
    
    if (conversation?.conversationType === 'direct' && conversation?.participants) {
      const otherParticipant = conversation?.participants?.find(p => p?.userId !== currentUser?.id);
      return otherParticipant?.user?.fullName || 'Unknown User';
    }
    
    return 'Group Chat';
  };

  const getConversationInfo = () => {
    if (conversation?.conversationType === 'group') {
      const participantCount = conversation?.participants?.length || 0;
      return `${participantCount} members`;
    }
    
    const otherParticipant = conversation?.participants?.find(p => p?.userId !== currentUser?.id);
    return otherParticipant?.user?.role || 'Online';
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-500">Loading messages...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center space-x-3">
          {conversation?.conversationType === 'group' ? (
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <Users className="w-5 h-5 text-green-600" />
            </div>
          ) : (
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <User className="w-5 h-5 text-blue-600" />
            </div>
          )}
          <div>
            <h3 className="font-medium text-gray-900">{getConversationTitle()}</h3>
            <p className="text-sm text-gray-500">{getConversationInfo()}</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          {conversation?.conversationType === 'direct' && (
            <button className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100">
              <Phone className="w-5 h-5" />
            </button>
          )}
          <button className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100">
            <MoreVertical className="w-5 h-5" />
          </button>
        </div>
      </div>
      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages?.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-gray-500">
              <p>No messages yet.</p>
              <p className="text-sm mt-1">Start the conversation!</p>
            </div>
          </div>
        ) : (
          messages?.map((message, index) => {
            const isOwnMessage = message?.senderId === currentUser?.id;
            const showSender = !isOwnMessage && (
              index === 0 || 
              messages?.[index - 1]?.senderId !== message?.senderId
            );

            return (
              <div
                key={message?.id}
                className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`max-w-xs lg:max-w-md ${isOwnMessage ? 'order-2' : 'order-1'}`}>
                  {showSender && (
                    <p className="text-xs text-gray-500 mb-1 px-2">
                      {message?.sender?.fullName || 'Unknown'}
                    </p>
                  )}
                  <div
                    className={`px-4 py-2 rounded-lg ${
                      isOwnMessage
                        ? 'bg-blue-600 text-white' :'bg-gray-100 text-gray-900'
                    }`}
                  >
                    <p className="text-sm">{message?.content}</p>
                    <div className={`flex items-center justify-between mt-1 ${
                      isOwnMessage ? 'text-blue-100' : 'text-gray-500'
                    }`}>
                      <span className="text-xs">{formatTime(message?.createdAt)}</span>
                      {isOwnMessage && (
                        <div className="ml-2">
                          {getMessageStatusIcon(message?.messageStatus)}
                        </div>
                      )}
                    </div>
                    {message?.phoneNumber && (
                      <div className={`text-xs mt-1 ${
                        isOwnMessage ? 'text-blue-200' : 'text-gray-600'
                      }`}>
                        📱 SMS to {message?.phoneNumber}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>
      {/* Message Input */}
      <div className="p-4 border-t border-gray-200">
        {showPhoneInput && (
          <div className="mb-3 p-3 bg-blue-50 rounded-lg">
            <label className="block text-sm font-medium text-blue-700 mb-2">
              Send SMS to phone number:
            </label>
            <input
              type="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e?.target?.value)}
              placeholder="+1234567890"
              className="w-full px-3 py-2 border border-blue-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <button
              onClick={() => setShowPhoneInput(false)}
              className="mt-2 text-sm text-blue-600 hover:text-blue-800"
            >
              Cancel SMS
            </button>
          </div>
        )}

        <form onSubmit={handleSendMessage} className="flex items-end space-x-3">
          <div className="flex-1">
            <div className="relative">
              <textarea
                value={messageText}
                onChange={(e) => setMessageText(e?.target?.value)}
                onKeyPress={(e) => {
                  if (e?.key === 'Enter' && !e?.shiftKey) {
                    e?.preventDefault();
                    handleSendMessage(e);
                  }
                }}
                placeholder="Type your message..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                rows="1"
                style={{ minHeight: '40px', maxHeight: '120px' }}
              />
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={() => setShowPhoneInput(!showPhoneInput)}
              className={`p-2 rounded-full transition-colors ${
                showPhoneInput 
                  ? 'bg-blue-100 text-blue-600' :'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
              }`}
              title="Send SMS"
            >
              <Phone className="w-5 h-5" />
            </button>

            <button
              type="button"
              className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
            >
              <Paperclip className="w-5 h-5" />
            </button>

            <button
              type="button"
              className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
            >
              <Smile className="w-5 h-5" />
            </button>

            <button
              type="submit"
              disabled={!messageText?.trim() || sending}
              className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {sending ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                <Send className="w-5 h-5" />
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MessagePanel;